import addDays from 'date-fns/addDays';
import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import type { FinancialStatementEntry, FinancialStatementFilter } from 'v2/interfaces/financial';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import apiService from 'v2/services/api';
import type { ApiService } from 'v2/services/api';

import { financialStatementService, InitialPagination } from '../statement';
import type { ListOrder } from '../statement';

class FinancialStatementFuture {
  private loading$ = new Rx.BehaviorSubject<boolean>(true);

  private financialStatementFuture$ = new Rx.BehaviorSubject<FinancialStatementEntry[]>(null);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  constructor(private apiServices: ApiService) {}
  public getFinancialStatementFuture = (): Rx.Observable<FinancialStatementEntry[]> => this.financialStatementFuture$;
  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public isLoading = () => this.loading$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public formatFilter = (filter: FinancialStatementFilter) => {
    const newFilter: Partial<FinancialStatementFilter> = {};
    const daysToAdd = parseInt(filter.periodPreset) - 1;
    const startDateObj = addDays(new Date(), 1);
    const startDate = formatDate(startDateObj, 'yyyy-MM-dd');
    const endDate = formatDate(addDays(startDateObj, daysToAdd), 'yyyy-MM-dd');

    newFilter.transacted_at_initial = startDate;
    newFilter.transacted_at_final = endDate;

    if (filter.search_text) {
      newFilter.search_type = filter.search_type;
      newFilter.search_text = filter.search_text;
    }

    return newFilter;
  };

  public loadData(order?: ListOrder) {
    return financialStatementService.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter =>
        this.resetPage().pipe(
          map(params => ({
            ...filter,
            ...{
              current_page: params.current_page < 0 ? 1 : params.current_page,
              per_page: params.per_page,
              order: order || 'DESC'
            }
          }))
        )
      ),
      debounceTime(10),
      switchMap(filter => {
        this.loading$.next(true);
        return this.apiServices
          .get('financial-statement/in-future', filter)
          .pipe(finalize(() => this.loading$.next(false)));
      }),
      tap(response => this.financialStatementFuture$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination))
    );
  }
}

export const financialStatementFutureService = new FinancialStatementFuture(apiService);
